import { useOutsideAlerter } from '@hooks';
import clsx from 'clsx';
import React, { ReactElement, useRef, useState } from 'react';

import { Icon } from '../Icon/Icon';

import styles from './Dropdown.module.scss';

export type DropdownItemType = {
  label: string | ReactElement;
  value: string | number;
};

interface DropdownProps {
  className?: string;
  items: DropdownItemType[];
  selected: DropdownItemType | null;
  includeBlank?: boolean;
  blankText?: string;
  onSelect: (item: DropdownItemType) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  className,
  items,
  selected,
  includeBlank,
  blankText,
  onSelect,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef(null);

  useOutsideAlerter(dropdownRef, setIsOpen.bind(null, false));

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (item: DropdownItemType) => {
    if (!disabled) {
      onSelect(item);
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={clsx(styles.dropdown, className, {
        [styles.isOpen]: isOpen,
        [styles.disabled]: disabled,
      })}
    >
      <div
        onClick={handleToggle}
        className={clsx(styles.selected, {
          [styles.haveSelected]: selected !== null,
          [styles.disabled]: disabled,
          [styles.placeholder]: placeholder && !selected,
        })}
      >
        <span>{selected?.label || placeholder}</span>
        <Icon name="angle-right" />
      </div>
      <div className={styles.list}>
        {includeBlank && (
          <div
            key="_blank"
            className={styles.item}
            onClick={() => handleSelect({ label: 'None', value: '' })}
          >
            {blankText ?? '- None'}
          </div>
        )}
        {items.map((item: DropdownItemType) => (
          <div
            key={item.value}
            className={styles.item}
            onClick={() => handleSelect(item)}
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};
